<template>
  <div id="calculator-app">
    <HeaderInner
        class="calculator-header"
        :breadcrumbs="breadcrumbs"
        resize-type="width"
        data-testid="btccalculator"
    >
      {{ heading }}
    </HeaderInner>
    <div class="container">
      <Calculator
          class="calculator-body"
          @fiat-changed="handleFiatChange"
          @crypto-changed="handleCryptoChange"
          @new-conversion="sendConversion"
      />
    </div>
    <PromoCampaign v-if="promoInfo" :promoInfo="promoInfo" />
    <div v-else class="calculator-buy-now">
      <CalculatorBuyNow/>
      <p class="text-grey">{{ $t('calculator.buyNow.hint') }}</p>
    </div>
    <CalculatorReLinkingBlock v-if="isPublicPage" />
  </div>
</template>

<script>
import '@/plugins/bus.js';

import debounce from 'lodash/debounce';
import { mapActions, mapMutations, mapState } from 'vuex';

import Calculator from '@/components/Calculator/Calculator.vue';
import CalculatorBuyNow from '@/components/Calculator/CalculatorBuyNow.vue';
import CalculatorReLinkingBlock from '@/components/Calculator/ReLinking/CalculatorReLinkingBlock.vue';
import HeaderInner from '@/components/HeaderInner/HeaderInner.vue';
import CalculatorUrlGenerator from '@/services/calculatorUrlGenerator';
import SEOClient from '@/services/clients/seo-client';
import { freezeBody } from '@/utilities/FreezeBody.js';

import calculatorEventNames from '../../../assets/js/events/calculator-event-names';
import eventBus from '../../../assets/js/helpers/event-bus';
import PromoCampaign from "@/components/Calculator/PromoCampaign.vue";

export default {
  name: 'CalculatorPage',
  components: {
    PromoCampaign,
    HeaderInner,
    CalculatorReLinkingBlock,
    CalculatorBuyNow,
    Calculator,
  },

  props: {
    promoInfo: { type: Object, required: false, default: null },
  },

  mounted () {
    this.$bus.$on('FREEZE_BODY', (doFreeze) => freezeBody(doFreeze));
  },

  computed: {
    ...mapState('calculator', [
      'selectedFiatCurrency',
      'selectedCryptoCurrency',
      'breadcrumbs',
      'heading',
      'isPublicPage',
    ]),
  },

  methods: {
    ...mapMutations({
      setHeading: 'calculator/setHeading',
      setBreadcrumbs: 'calculator/setBreadcrumbs',
    }),
    ...mapActions({
      requestUsdAggregatePrice: 'calculator/requestUsdAggregatePrice',
    }),
    handleCryptoChange (cryptoCurrency) {
      this.requestUsdAggregatePrice(cryptoCurrency);
      this.updatePageAttributes(cryptoCurrency, this.selectedFiatCurrency);
    },
    handleFiatChange (fiatCurrency) {
      this.updatePageAttributes(this.selectedCryptoCurrency, fiatCurrency);
    },
    sendConversion: debounce(({
      cryptoCurrency,
      fiatCurrency,
      cryptoAmount,
    }) => {
      const formattedAmount = cryptoAmount.toString();
      const [integerNumber, decimalNumber] = formattedAmount.split('.');
      const integerLength = integerNumber.length;
      const fractionalLength = decimalNumber ? decimalNumber.length : 0;

      if (isNaN(formattedAmount) || formattedAmount <= 0 || integerLength > 15 || fractionalLength > 15) {
        return;
      }
      SEOClient.postConversion(
        formattedAmount,
        cryptoCurrency.code,
        fiatCurrency.code,
      );
    }, 1000),
    updatePageAttributes (cryptoCurrency, fiatCurrency) {
      this.setPageTitle(cryptoCurrency, fiatCurrency);
      this.setPageUrl(cryptoCurrency, fiatCurrency);
      this.setPageHeading(cryptoCurrency, fiatCurrency);
      this.setPageBreadcrumbs(cryptoCurrency, fiatCurrency);
      eventBus.emit(calculatorEventNames.CALCULATOR_CHANGE_CURRENCY, {
        cryptoCurrency,
        fiatCurrency,
      });
    },
    setPageHeading (cryptoCurrency, fiatCurrency) {
      const newHeading = this.$t('calculator.pageHeading.cryptoToFiatNoAmount', {
        cryptoCode: cryptoCurrency.code,
        fiatCode: fiatCurrency.code,
      });

      this.setHeading(newHeading);
    },
    setPageBreadcrumbs (cryptoCurrency, fiatCurrency) {
      if (!this.isPublicPage) {
        return;
      }

      const newBreadcrumbs = [
        {
          label: this.$t('calculator.breadcrumb.index', {
            cryptoName: cryptoCurrency.name,
          }),
          url: CalculatorUrlGenerator.crypto(cryptoCurrency.slug, this.$i18n.locale),
        },
        {
          label: this.$t('calculator.breadcrumb.cryptoToFiatNoAmount', {
            cryptoCode: cryptoCurrency.code,
            fiatCode: fiatCurrency.code,
          }),
          url: CalculatorUrlGenerator.cryptoToFiat(cryptoCurrency.code, fiatCurrency.code, this.$i18n.locale),
        },
      ];

      this.setBreadcrumbs(newBreadcrumbs);
    },
    setPageTitle (cryptoCurrency, fiatCurrency) {
      const newTitle = this.$t('calculator.pageTitle.cryptoToFiatNoAmount', {
        cryptoCode: cryptoCurrency.code,
        fiatCode: fiatCurrency.code,
        cryptoName: cryptoCurrency.name,
        fiatName: fiatCurrency.name,
      });

      document.title = `${newTitle} | Paybis`;
    },
    setPageUrl (cryptoCurrency, fiatCurrency) {
      if (!this.isPublicPage) {
        return;
      }

      const newUrl = CalculatorUrlGenerator.cryptoToFiat(cryptoCurrency.code, fiatCurrency.code, this.$i18n.locale);

      history.replaceState({}, null, newUrl);
    },
  },
};
</script>
